import React, { useState } from 'react';
import { CatchRecord, filterSeasonCatches, computeSeasonStats, speciesLabel } from '../../utils/memberStats';

interface SeasonStatsCardProps {
  catches: CatchRecord[];
  membershipType: string;
  familyMembers: Array<{ name: string }> | null;
  familyCatches?: CatchRecord[];
}

const SeasonStatsCard: React.FC<SeasonStatsCardProps> = ({ catches, membershipType, familyMembers, familyCatches }) => {
  const [familyOpen, setFamilyOpen] = useState(false);
  const year = new Date().getFullYear();
  const seasonCatches = filterSeasonCatches(catches, year);
  const stats = computeSeasonStats(seasonCatches);

  const tile = (label: string, value: string) => (
    <div className="bg-gray-50 rounded-lg p-3 text-center">
      <div className="text-lg font-bold text-gray-900">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-8">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Season Stats ({year})</h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {tile('Total Catches', stats.totalCatches > 0 ? String(stats.totalCatches) : '—')}
        {tile('Longest Catch', stats.longestCatch ? `${speciesLabel(stats.longestCatch.species)} — ${stats.longestCatch.lengthCm}cm` : '—')}
        {tile('Competitions Entered', stats.competitionsEntered > 0 ? String(stats.competitionsEntered) : '—')}
        {tile('Kept / Released', stats.keptPercent != null ? `${stats.keptPercent}% / ${stats.releasedPercent}%` : '—')}
      </div>
      {seasonCatches.length === 0 && (
        <p className="text-sm text-gray-500 text-center mt-3">No catches recorded this season</p>
      )}
      {membershipType === 'FAMILY' && familyMembers && familyMembers.length > 0 && familyCatches && (
        <div className="mt-4">
          <button
            onClick={() => setFamilyOpen(!familyOpen)}
            className="text-sm text-fishing-blue hover:underline"
          >
            {familyOpen ? 'Hide' : 'Show'} Family Breakdown
          </button>
          {familyOpen && (
            <div className="mt-2 space-y-2">
              {familyMembers.map((fm: any) => {
                const fmCatches = filterSeasonCatches(
                  familyCatches.filter((c: CatchRecord) => c.competitorName === fm.name),
                  year
                );
                const fmStats = computeSeasonStats(fmCatches);
                return (
                  <div key={fm.name} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2 text-sm">
                    <span className="font-medium text-gray-900">{fm.name}</span>
                    <span className="text-gray-600">
                      {fmStats.totalCatches} catch{fmStats.totalCatches !== 1 ? 'es' : ''}
                      {fmStats.longestCatch ? ` · Best: ${fmStats.longestCatch.lengthCm}cm` : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SeasonStatsCard;
